<template>
  <Header :title="$t('userManagement.title')" />
  <div v-if="readPermission" class="userManagementContainer">
    <div class="pageContainerJob">
      <!-- Loading Spinner -->
      <div class="customRowTenant">
        <p class="jobHistoryTitle" style="text-align: left">{{ $t('userManagement.users') }}</p>
        <div class="searchBar">
          <button v-if="search" class="resetButton" @click="resetSearch">×</button>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
            <circle cx="6.5" cy="7.45996" r="5.75" stroke="#8F969B" stroke-width="1.5" />
            <path d="M11 11.96L15 15.96" stroke="#8F969B" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          <input type="text" class="searchInput" :placeholder="$t('userManagement.search_placeholder')" v-model="search" @input="handleSearch" />
        </div>
      </div>

      <div v-if="loading" style="width: 100%; display: flex; justify-content: center; align-items: center; height: 100%">
        <div class="spinner-border text-primary spinner-grow-xl" role="status"></div>
      </div>

      <!-- User Table -->
      <div v-if="!loading" class="">
        <div class="tableHeadingsNew">
          <div class="customRow">
            <CustomDropdown :label="$t('userManagement.select_tenant')" :placeholder="$t('userManagement.select_tenant')" v-model="selectedTenantId" :options="tenants" :error="roleError" />
            <div class="tableVr"></div>
            <img src="/backward.svg" alt="Back" style="cursor: pointer" @click="goToPreviousPage" />
            <div class="pageDiv">
              <span class="pageDivText">{{ page }} / {{ totalPages }}</span>
            </div>
            <span class="pageOfTitle">Page {{ page }} of {{ totalPages }}</span>
            <img src="/forward.svg" alt="Next" style="cursor: pointer" @click="goToNextPage" />
            <div class="pageDiv" @click.stop="togglePageSizeDropdown">
              <span class="pageDivText">{{ pageSize }}</span>
              <img src="/arrowDownNew.png" class="arrowDownNew" alt="" />

              <ul ref="dropdownEl" v-if="showPageSizeDropdown" class="pageSizeDropdown" @click.stop>
                <li v-for="size in pageSizeOptions" :key="size" @click.stop="selectPageSize(size)">
                  {{ size }}
                </li>
              </ul>
            </div>
          </div>

          <div class="customRow">
            <Filter @filter-applied="handleFilterChange" ref="filterComponent" :initial-active="isActive === true ? 'Yes' : isActive === false ? 'No' : ''" :initial-role="selectedRole" />
            <SecondaryButton v-if="writePermission" @click="handleAddUser">{{ $t('userManagement.add_new_user') }}</SecondaryButton>
          </div>
        </div>

        <div class="tableContainer">
          <table class="customTable">
            <thead class="customTableHeader">
              <tr class="customTableRow">
                <th>{{ $t('userManagement.table.sr_no') }}</th>
                <th>{{ $t('userManagement.table.first_name') }}</th>
                <th>{{ $t('userManagement.table.last_name') }}</th>
                <th>{{ $t('userManagement.table.tenant_name') }}</th>
                <th>{{ $t('userManagement.table.email') }}</th>
                <th>{{ $t('userManagement.table.phone') }}</th>
                <th>{{ $t('userManagement.table.status') }}</th>
                <th>{{ $t('userManagement.table.role') }}</th>
                <th>{{ $t('userManagement.table.is_active') }}</th>
                <th>{{ $t('userManagement.table.actions') }}</th>
              </tr>
            </thead>
            <tbody class="customTableBody">
              <!-- Dynamically create a row for each job -->
              <tr v-for="(user, index) in users" :key="user.id" style="height: 50px !important">
                <td>{{ index + 1 }}</td>
                <td class="truncate" :title="user.first_name">
                  {{ truncateText(user.first_name, 20) }}
                </td>
                <td class="truncate" :title="user.last_name">
                  {{ truncateText(user.last_name, 20) }}
                </td>
                <td class="truncate" :title="user.tenant.tenant_name">
                  {{ truncateText(user.tenant.tenant_name, 30) }}
                </td>
                <td class="truncate" :title="user.email_id">
                  {{ truncateText(user.email_id, 30) }}
                </td>
                <td class="truncate" :title="user.phone_number">
                  {{ truncateText(user.phone_number, 12) }}
                </td>
                <td class="truncate" :title="user.status">
                  {{ truncateText(user.status, 12) }}
                </td>
                <td class="truncate" :title="user.user_roles?.map((r) => r.role.roleName).join(', ')">
                  <span v-for="(userRole, index) in user.user_roles" :key="userRole.id"> {{ truncateText(userRole.role.roleName, 12) }}<span v-if="index < user.user_roles.length - 1">, </span> </span>
                </td>
                <td>
                  <div v-if="user.status !== 'Invited'" class="toggle-wrapper">
                    <CustomToggle v-if="writePermission" :modelValue="user.status !== 'Inactive'" @update:modelValue="(value) => handleStatusToggle(user.id, value)" />
                    <span v-else>{{ user.status !== 'Inactive' ? $t('userManagement.status.yes') : $t('userManagement.status.no') }}</span>
                  </div>
                </td>

                <td style="display: flex; flex-direction: row; gap: 13px; justify-content: flex-start; align-items: center; height: 100%; margin-top: 10px">
                  <CustomHyperlink v-if="writePermission" :href="getEditUserUrl(user.id)">{{ $t('userManagement.actions.edit') }}</CustomHyperlink>
                  <CustomHyperlink v-if="writePermission && user.email_id.toLowerCase() !== userData.email_id.toLowerCase()" @click.prevent="handleRoleChange(user.id)">{{ $t('userManagement.actions.change_role') }}</CustomHyperlink>
                  <CustomHyperlink v-if="writePermission && user.status === 'Invited'" @click.prevent="handleResendInvite(user.email_id)"> Resend Invite </CustomHyperlink>
                  <CustomHyperlink v-if="writePermission && user.status === 'Invited' && userRole === 'Super Admin'" @click.prevent="hanleShowConfirmationPopup(user.email_id)"> Delete User </CustomHyperlink>
                </td>
              </tr>
              <tr v-if="users.length === 0" class="no-data-row">
                <td colspan="12" class="no-data-message">
                  <div class="no-data-container">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="12"></line>
                      <line x1="12" y1="16" x2="12.01" y2="16"></line>
                    </svg>
                    <h3>{{ $t('userManagement.no_data.title') }}</h3>
                    <!-- <p>There are no jobs to display at the moment.</p> -->
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
  <div v-if="!readPermission">
    <PermissionAlert :message="$t('common.no_permission')" />
  </div>

  <!-- Replace the existing confirmation modal with: -->
  <ConfirmationModal :show="showConfirmModal" :message="$t('userManagement.modals.confirm_status.message')" @close="closeConfirmModal" @confirm="confirmStatusChange" />
  <DeleteConfirmationModal :show="showDeleteConfirmModal" @close="handleDeleteConfirmClose" @confirm="handleDeleteUser" :message="delelteConfirmationMessage"/>
  <!-- Add this new modal for role change -->
  <div v-if="showRoleModal" class="modal-overlay" @click="handleClickAway">
    <div class="role-modal" ref="roleModal" @click.stop>
      <button class="close-button" @click="closeRoleModal">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M4.16504 4.16602L15.8309 15.8319" stroke="#5A5C62" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M4.1642 15.8319L15.8301 4.16602" stroke="#5A5C62" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>

      <div class="modal-content">
        <div class="modal-body-header">
          <span class="modal-title-new">{{ $t('userManagement.modals.change_role.title') }}</span>
          <p class="modal-message-new">{{ $t('userManagement.modals.change_role.message') }}</p>
        </div>

        <div class="roles-container">
          <div v-for="role in roles" :key="role.id" class="role-option" @click="selectedRole = role.id">
            <svg v-if="selectedRole === role.id" xmlns="http://www.w3.org/2000/svg" width="26" height="27" viewBox="0 0 26 27" fill="none">
              <path d="M13 24.3857C6.7275 24.3857 1.625 19.2832 1.625 13.0107C1.625 6.73824 6.7275 1.63574 13 1.63574C19.2725 1.63574 24.375 6.73824 24.375 13.0107C24.375 19.2832 19.2725 24.3857 13 24.3857ZM13 3.26074C7.62125 3.26074 3.25 7.63199 3.25 13.0107C3.25 18.3895 7.62125 22.7607 13 22.7607C18.3787 22.7607 22.75 18.3895 22.75 13.0107C22.75 7.63199 18.3787 3.26074 13 3.26074Z" fill="var(--primary-color)" />
              <path d="M13 19.5107C16.5899 19.5107 19.5 16.6006 19.5 13.0107C19.5 9.42089 16.5899 6.51074 13 6.51074C9.41015 6.51074 6.5 9.42089 6.5 13.0107C6.5 16.6006 9.41015 19.5107 13 19.5107Z" fill="var(--primary-color)" />
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="26" height="27" viewBox="0 0 26 27" fill="none">
              <path d="M13 24.3857C6.7275 24.3857 1.625 19.2832 1.625 13.0107C1.625 6.73824 6.7275 1.63574 13 1.63574C19.2725 1.63574 24.375 6.73824 24.375 13.0107C24.375 19.2832 19.2725 24.3857 13 24.3857ZM13 3.26074C7.62125 3.26074 3.25 7.63199 3.25 13.0107C3.25 18.3895 7.62125 22.7607 13 22.7607C18.3787 22.7607 22.75 18.3895 22.75 13.0107C22.75 7.63199 18.3787 3.26074 13 3.26074Z" fill="var(--primary-color)" />
            </svg>
            <span>{{ role.roleName }}</span>
          </div>
        </div>

        <div class="modal-buttons">
          <button class="cancel-button" @click="closeRoleModal">{{ $t('userManagement.buttons.cancel') }}</button>
          <button class="confirm-button" @click="confirmRoleChange">{{ $t('userManagement.modals.change_role.done') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from '../../components/Header.vue'
import { get, put, post } from '../../utils/api.js'
import CustomToggle from '../../components/CustomToggle/CustomToggle.vue'
import CustomHyperlink from '../../components/CustomHyperlink/CustomHyperlink.vue'
import CustomDropdown from '../../components/CustomDropdown/CustomDropdown.vue'
import Filter from '../../components/Filter/Filter.vue'
import SecondaryButton from '../../components/SecondaryButton/SecondaryButton.vue'
import { showToast } from '@/utils/toast'
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal.vue'
import PermissionAlert from '@/components/PermissionAlert.vue'
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal/DeleteConfirmationModal.vue'

export default {
  components: {
    Header,
    CustomToggle,
    CustomHyperlink,
    CustomDropdown,
    Filter,
    SecondaryButton,
    ConfirmationModal,
    PermissionAlert,
    DeleteConfirmationModal,
  },
  data() {
    let userData = null
    try {
      const userDataStr = localStorage.getItem('UserData')
      if (userDataStr) {
        userData = JSON.parse(userDataStr)
      }
    } catch (error) {
    }
    return {
      roles: [],
      tenants: [],
      loading: false,
      users: [],
      selectedTenantId: '',
      page: 1,
      pageSize: 10,
      total: 0,
      userRole: localStorage.getItem('userRole'),
      totalPages: 1,
      userData: userData,
      pageSizeOptions: [10, 20, 50, 100],
      showPageSizeDropdown: false,
      search: '',
      isActive: null,
      debounceTimeout: null,
      showConfirmModal: false,
      pendingStatusChange: null,
      showRoleModal: false,
      selectedRole: null,
      selectedUserId: null,
      readPermission: false,
      writePermission: false,
      userToDelete: null,
      showDeleteConfirmModal: false,
      delelteConfirmationMessage:"Are you sure you want to delete this user ?"
    }
  },
  mounted() {
    // const { tenantId } = this.$route.query
    // if (tenantId) this.selectedTenantId = tenantId
    document.title = 'User Management'
    this.checkPermissions()
    this.fetchUsers()
    this.fetchTenants()
    this.fetchRoles()
    document.addEventListener('click', this.handleClickAway)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickAway)
  },
  methods: {
    checkPermissions() {
      const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
      this.readPermission = permissions.some((p) => p.object_name === 'USERS_RD' && p.is_active)
      this.writePermission = permissions.some((p) => p.object_name === 'USERS_WR' && p.is_active)
    },
    handleClickAway(event) {
      const dropdownEl = this.$refs.dropdownEl
      const roleModal = this.$refs.roleModal

      if (dropdownEl && !dropdownEl.contains(event.target)) {
        this.showPageSizeDropdown = false
        this.showEditCategoryDropdown = false
      }

      if (this.showRoleModal && roleModal && !roleModal.contains(event.target)) {
        this.closeRoleModal()
      }
    },
    togglePageSizeDropdown() {
      this.showPageSizeDropdown = !this.showPageSizeDropdown
    },
    async fetchRoles() {
      this.loading = true
      try {
        const res = await get('/v1/roles')
        if (res?.data?.code === 200) {
          this.roles = res.data.data.roles?.filter((role) => role.roleName !== 'SYSTEM ROLE') || []
        }
      } catch (err) {
      } finally {
        this.loading = false
      }
    },
    async fetchTenants() {
      this.loading = true
      try {
        const res = await get('/v1/tenants?page=1&pageSize=1000000')
        if (res?.data?.code === 200) {
          this.tenants = (res.data.data.tenants || []).map((tenant) => ({
            label: tenant.tenant_name,
            value: tenant.id,
          }))
          if (this.userRole !== 'Super Admin') {
            this.selectedTenantId = this?.tenants?.[0]?.value
          } else {
            this.selectedTenantId = ''
          }
        }
      } catch (err) {
      } finally {
        this.loading = false
      }
    },
    async handleResendInvite(email) {
      try {
        const response = await post(`/auth/resend-invite`, { email })

        if (response?.data?.code === 200) {
          showToast({
            title: 'Success',
            message: 'Invitation email has been resent successfully.',
            type: 'success',
          })
        } else {
          throw new Error('Unexpected response')
        }
      } catch (error) {
        showToast({
          title: 'Failed',
          message: 'Unable to resend invitation. Please try again later.',
          type: 'error',
        })
      }
    },
    async handleDeleteUser() {
      console.log("this.userToDelete===>", this.userToDelete)
      if(this.userToDelete != null){
      try {
        const email = this.userToDelete
        const response = await post(`/auth/delete-user`, { email })
        if (response?.data?.code === 200) {
          showToast({
            title: 'Success',
            message: 'User Deleted Successfully.',
            type: 'success',
          })
          this.fetchUsers()
        } else {
          throw new Error('Unexpected response')
        }
      } catch (error) {
        showToast({
          title: 'Failed',
          message: 'Failed to delete user. Please try again later.',
          type: 'error',
        })
      }finally{
        this.showDeleteConfirmModal = false
      }
    }
    },
    async selectPageSize(size) {
      this.pageSize = size
      this.page = 1
      this.showPageSizeDropdown = false
      await this.fetchUsers()
    },
    async fetchUsers() {
      try {
        this.loading = true
        let url = `/v1/users?page=${this.page}&pageSize=${this.pageSize}`

        if (this.search) {
          url += `&search=${encodeURIComponent(this.search)}`
        }
        if (this.selectedTenantId) {
          url += `&tenantId=${this.selectedTenantId}`
        }
        if (this.isActive !== null) {
          url += `&isActive=${this.isActive}`
        }
        if (this.selectedRole) {
          url += `&roleId=${this.selectedRole}`
        }

        const response = await get(url)
        if (response?.data?.code === 200) {
          const data = response.data.data
          this.users = response.data.data.users || []
          this.total = data.total
          this.totalPages = Math.ceil(this.total / this.pageSize)
        } else {
          this.users = []
        }
      } catch (error) {
      } finally {
        this.loading = false
      }
    },
    formatDate(dateStr) {
      const dateObj = new Date(dateStr)
      return dateObj.toLocaleDateString()
    },
    prevPage() {
      if (this.page > 1) {
        this.page--
        this.fetchUsers()
      }
    },
    nextPage() {
      if (this.page < this.totalPages) {
        this.page++
        this.fetchUsers()
      }
    },
    handleAddUser() {
      const tenantName = localStorage.getItem('url_name')
      this.$router.push(`/${tenantName}/create-user`)
    },

    async goToPreviousPage() {
      if (this.page > 1) {
        this.page--
        await this.fetchUsers()
      }
    },
    async goToNextPage() {
      if (this.page < this.totalPages) {
        this.page++
        await this.fetchUsers()
      }
    },
    async updatePageSize(event) {
      this.pageSize = parseInt(event.target.value)
      this.page = 1 // reset to first page
      await this.fetchUsers()
    },
    handleSearch() {
      clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(() => {
        this.fetchUsers()
      }, 300)
    },
    handleFilterChange(filters) {
      this.isActive = filters.active === 'Yes' ? true : filters.active === 'No' ? false : null
      this.selectedRole = filters.role
      this.page = 1
      this.fetchUsers()
    },
    resetSearch() {
      this.search = ''
      this.fetchUsers()
    },
    resetAllFilters() {
      this.search = ''
      this.selectedTenantId = ''
      this.isActive = null
      this.selectedRole = null
      if (this.$refs.filterComponent) {
        this.$refs.filterComponent.resetFilters()
      }
      this.page = 1
      this.fetchUsers()
    },
    async updateUserStatus(userId, value) {
      try {
        const response = await put(`/v1/users/${userId}`, {
          status: value ? 'Active' : 'Inactive',
          is_active: value,
          first_name: this.users.find((u) => u.id === userId)?.first_name || null,
          last_name: this.users.find((u) => u.id === userId)?.last_name || null,
          phone_number: this.users.find((u) => u.id === userId)?.phone_number || null,
          tenant_id: this.users.find((u) => u.id === userId)?.tenant_id || 2,
          email_id: this.users.find((u) => u.id === userId)?.email_id || null,
        })

        if (response?.data?.code === 200) {
          const currentUser = this.users.find((u) => u.id === userId)
          if (currentUser) {
            currentUser.status = value ? 'Active' : 'Inactive'
          }
          showToast({
            title: this.$t('userManagement.messages.operation_successful'),
            message: this.$t('userManagement.messages.success.status_changed', { status: value ? this.$t('userManagement.status.active').toLowerCase() : this.$t('userManagement.status.inactive').toLowerCase() }),
            type: 'success',
          })
        }
      } catch (error) {
        showToast({
          title: this.$t('userManagement.messages.operation_failed'),
          message: this.$t('userManagement.messages.error.status_update_failed'),
          type: 'error',
        })
      }
    },
    handleStatusToggle(userId, value) {
      this.pendingStatusChange = { userId, value }
      this.showConfirmModal = true
    },
    closeConfirmModal() {
      this.showConfirmModal = false
      this.pendingStatusChange = null
    },
    handleDeleteConfirmClose() {
      this.delelteConfirmationMessage = "Are you sure you want to delete this user ?"
      this.showDeleteConfirmModal = false
      this.userToDelete = null
    },
    hanleShowConfirmationPopup(email){
      this.delelteConfirmationMessage = `Are you sure you want to delete this user - "${email}" ?`
      this.showDeleteConfirmModal = true,
      this.userToDelete = email
    },
    async confirmStatusChange() {
      if (this.pendingStatusChange) {
        const { userId, value } = this.pendingStatusChange
        await this.updateUserStatus(userId, value)
        this.closeConfirmModal()
      }
    },
    async handleRoleChange(userId) {
      try {
        // First fetch the current user role
        const response = await get(`/v1/user-roles/${userId}`)

        if (response?.data?.code === 200 && response.data.data.length > 0) {
          // Set the current role as selected
          this.selectedRole = response.data.data[0].roleId
        }

        this.selectedUserId = userId
        this.showRoleModal = true
      } catch (error) {
        showToast({
          title: this.$t('userManagement.messages.error_title'),
          message: this.$t('userManagement.messages.error.role_fetch_failed'),
          type: 'error',
        })
      }
    },
    closeRoleModal() {
      this.showRoleModal = false
      this.selectedRole = null
      this.selectedUserId = null
    },
    async confirmRoleChange() {
      if (!this.selectedRole || !this.selectedUserId) return

      try {
        const response = await put(`/v1/user-roles/update`, {
          userId: this.selectedUserId,
          roleIds: [this.selectedRole],
        })

        if (response?.data?.code === 200) {
          showToast({
            title: this.$t('userManagement.messages.success_title'),
            message: this.$t('userManagement.messages.success.user_updated'),
            type: 'success',
          })
          this.closeRoleModal()
          this.fetchUsers()
        }
      } catch (error) {
        showToast({
          title: this.$t('userManagement.messages.error_title'),
          message: this.$t('userManagement.messages.error.role_update_failed'),
          type: 'error',
        })
      }
    },
    truncateText(text, maxLength) {
      if (!text) return ''
      if (text.length <= maxLength) return text
      return text.slice(0, maxLength) + '...'
    },
    getEditUserUrl(userId) {
      const tenantName = localStorage?.getItem('url_name') || ''
      return `/${tenantName}/edit-user/${userId}`
    },
  },
  watch: {
    selectedTenantId(newValue) {
      // Reset to first page when tenant changes
      this.page = 1
      this.fetchUsers()
    },
  },
}
</script>

<style scoped>
.pagination {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

.pagination button {
  padding: 5px 10px;
  cursor: pointer;
}

.pageSizeDropdownNew {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-top: 4px;
  list-style: none;
  padding: 4px 0;
  width: 100%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.pageSizeDropdownNew li {
  padding: 6px 12px;
  cursor: pointer;
  font-family: 'Archivo', sans-serif;
  font-size: 14px;
  color: #5c656c;
}

.pageDiv {
  position: relative;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #ccc;
  padding: 6px 12px;
  border-radius: 4px;
  background: #fff;
}

.resetButton {
  position: absolute;
  right: 30px;
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #8f969b;
}

.searchBar {
  position: relative;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.confirm-modal {
  display: flex;
  width: 405px;
  height: 282px;
  padding: 24px 40px 23px 40px;
  align-items: flex-start;
  flex-shrink: 0;
  border-radius: 6px;
  background: #fff;
  position: relative;
}

.close-button {
  width: 20px;
  height: 20px;
  position: absolute;
  right: 14px;
  top: 14px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.modal-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.modal-title {
  color: var(--primary-color);
  text-align: left;
  font-family: Archivo;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 40px;
  margin-bottom: 8px;
}

.modal-title-new {
  color: var(--primary-color);
  text-align: left;
  font-family: Archivo;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 40px;
  margin-bottom: 8px;
  width: 100%;
}

.modal-message {
  overflow: hidden;
  color: #667085;
  text-overflow: ellipsis;
  font-family: Archivo;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 25px;
  /* 178.571% */
}

.modal-message-new {
  color: #667085;
  text-overflow: ellipsis;
  font-family: Archivo;
  text-align: left;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 25px;
  /* 178.571% */
  width: 100%;
}

.modal-buttons {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.modal-buttons button {
  width: 123.915px;
  height: 35px;
  flex-shrink: 0;
  border-radius: 4px;
  cursor: pointer;
  font-family: Archivo;
  font-weight: 500;
}

.cancel-button {
  background: transparent;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
}

.confirm-button {
  background: var(--primary-color);
  border: none;
  color: white;
}

.modal-content-inner {
  display: flex;
  width: 100px;
  height: 100px;
  padding: var(--Micro-XL, 12px);
  justify-content: center;
  align-items: center;
  border-radius: 50px;
  background-color: #f0f9fd;
  border-radius: 50%;
}

.role-modal {
  display: flex;
  width: 405px;
  height: 322px;
  padding: 24px 24px 23px 24px;
  align-items: flex-start;
  flex-shrink: 0;
  border-radius: 6px;
  background: #fff;
  position: relative;
}

.roles-container {
  width: 100%;
  margin: 20px 0;
}

.role-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  cursor: pointer;
}

.role-option span {
  color: var(--primary-color);
  font-family: Archivo;
  font-size: 16px;
  font-weight: 500;
}

.modal-body-header {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  border-bottom: 1px solid #d9d9d9;
  padding-bottom: -20px;
}

.tableContainer {
  max-height: calc(100vh - 250px);
  margin-top: 17px;
  overflow-y: auto;
  /* Enable vertical scrolling */
  min-height: 0;
  /* Important for flex container */
}

.no-records-message {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #667085;
  font-family: Archivo;
  font-size: 16px;
  font-weight: 500;
}

.tableHeadingsNew {
  margin-top: 24px;
}
</style>
